import { User } from '@/models/User';
import ProjectForumService, { ProjectViewerReference } from '@/services/ProjectForumService';
import { getUser } from '@/services/Profile/Api';

export interface ProjectViewersLoadResult {
  users: User[];
  failedUserIds: string[];
  requestedUserCount: number;
}

interface ProjectViewerResult {
  userId: string;
  user?: User;
}

const getViewerId = (viewer: ProjectViewerReference): string => {
  if (typeof viewer === 'string') return viewer;
  return viewer.userId ?? viewer.id ?? '';
};

export const uniqueViewerIds = (viewers: ProjectViewerReference[]): string[] => Array.from(
  new Set(viewers.map(getViewerId).filter(Boolean)),
);

const compareText = (left = '', right = '') => left.localeCompare(
  right,
  undefined,
  { sensitivity: 'base' },
);

export const sortProjectViewers = (users: User[]): User[] => [...users].sort((left, right) => (
  compareText(left.firstname, right.firstname)
  || compareText(left.lastname, right.lastname)
  || compareText(left.userId, right.userId)
));

export const loadProjectViewers = async (projectId: string): Promise<ProjectViewersLoadResult> => {
  const userIds = uniqueViewerIds(await ProjectForumService.getProjectViewerIds(projectId));
  const results = await Promise.all(userIds.map(async (userId): Promise<ProjectViewerResult> => {
    try {
      return { userId, user: await getUser(userId) };
    } catch (_error) {
      return { userId };
    }
  }));

  return {
    users: results.flatMap(({ user }) => (user ? [user] : [])),
    failedUserIds: results.filter(({ user }) => !user).map(({ userId }) => userId),
    requestedUserCount: userIds.length,
  };
};
